import { Agency, agencyBasic } from '@app/models';
import { DictionaryEntity, OperationPoint, operationPoint, PcsEntity } from '@app/pcs/models';
import { Calendar, calendarWatermarkDetailedFragment } from '@app/pcs/models/calendar';
import { EntityType, PropertyNameMapper } from '@app/shared/forms/property-mapper';
import { TrainComposition } from '@dossier/models/train-composition';
import { gql } from 'apollo-angular';


export enum CargoCompositionItemStretchPointType {
  GRETZPUNKT_POINT = 'B',
  RICHTPUNKT_POINT = 'D'
}

export interface CargoCompositionItemStretch {
  id?: number;
  cargoCompositionItemId?: number;
  sortOrder?: number;
  cargoFromToPointType?: DictionaryEntity;
  fromPoint?: string;
  toPoint?: string;
}

export interface AddNewCargoCompositionItemStretch {
  cargoCompositionItemId: number;
  controlId: string;
}

export interface RemoveCargoCompositionItemStretch {
  controlId: string;
  index: number;
}

export interface GoodType {
  id: number;
  description: string;
}

export interface CargoCompositionItem {
  id?: number;
  trainCompositionId?: number;
  agency?: Agency;
  sortOrder?: number;
  impNo?: string;
  section?: number;
  sectionFrom?: OperationPoint;
  sectionTo?: OperationPoint;
  group?: number;
  groupFrom?: OperationPoint;
  groupTo?: OperationPoint;
  calendar?: Calendar;
  trafficPeriod?: string;
  trafficPeriodRemark?: string;
  load?: number;
  length?: number;
  goodType?: GoodType;
  trustedChangeOverFrom?: OperationPoint;
  trustedChangeOverTo?: OperationPoint;
  profile1?: string;
  profile2?: string;
  freeText?: string;
  waggonChangeOverFrom?: string;
  waggonChangeOverTo?: string;
  vmax?: number;
  banWaggonType?: DictionaryEntity;
  cargoBrakeTypes?: DictionaryEntity[];
  addition?: boolean;
  richtpunkts?: CargoCompositionItemStretch[];
  grenzpunkts?: CargoCompositionItemStretch[];
  impNoCheck?: boolean;
}

export interface CargoTrainComposition extends TrainComposition {
  compositionItems?: CargoCompositionItem[];
}

export const cargoTrainCompositionMapper = new PropertyNameMapper({
  trainHeadNote1: 'trainHeadNote1',
  trainHeadNote2: 'trainHeadNote2',
  freeText: 'freeText',
  compositionItems: new PropertyNameMapper({
    impNo: 'nationalCompNr',
    section: 'compSection',
    sectionFrom: 'compSectionFrom',
    sectionTo: 'compSectionTo',
    group: 'compGroup',
    groupFrom: 'compGroupFrom',
    groupTo: 'compGroupTo',
    calendar: 'calendarItem',
    trafficPeriod: 'groupTp',
    trafficPeriodRemark: 'groupTpRemark',
    load: 'load',
    length: 'length',
    trustedChangeOverFrom: 'trustedChangeOverFrom',
    trustedChangeOverTo: 'trustedChangeOverTo',
    profile1: 'profile1',
    profile2: 'profile2',
    freeText: 'freetext',
    vmax: 'vmax',
    banWaggonType: 'banWaggonId',
    cargoBrakeTypes: 'cargoBrakeTypes',
    addition: 'isAddition',
    richtpunkts: new PropertyNameMapper({
      fromPoint: 'fromPoint',
      toPoint: 'toPoint'
    }, {entityName: PcsEntity.CargoCompositionItemStretch, entityType: EntityType.Array}),
    grenzpunkts: new PropertyNameMapper({
      fromPoint: 'fromPoint',
      toPoint: 'toPoint'
    }, {entityName: PcsEntity.CargoCompositionItemStretch, entityType: EntityType.Array})
  }, {entityName: PcsEntity.CargoTrainCompositionItem, entityType: EntityType.Array})
}, {entityName: PcsEntity.CargoTrainComposition});


const cargoCompositionItemStretch = gql`
  fragment cargoCompositionItemStretch on CargoCompositionItemStretch {
    id
    cargoCompositionItemId
    sortOrder
    cargoFromToPointType {
      id
      description
    }
    fromPoint
    toPoint
  }
`;

export const cargoTrainCompositionItem = gql`
  fragment cargoTrainCompositionItem on CargoCompositionItem {
    id
    trainCompositionId
    agency {
      ...agencyBasic
    }
    sortOrder
    impNo
    section
    sectionFrom {
      ...operationPoint
    }
    sectionTo {
      ...operationPoint
    }
    group
    groupFrom {
      ...operationPoint
    }
    groupTo {
      ...operationPoint
    }
    trafficPeriod
    calendar {
      calendarWatermark {
        ...calendarWatermarkDetailed
      }
    }
    trafficPeriodRemark
    load
    length
    goodType {
      id
      description
      enee_id
    }
    trustedChangeOverFrom {
      ...operationPoint
    }
    trustedChangeOverTo {
      ...operationPoint
    }
    profile1
    profile2
    freeText
    waggonChangeOverFrom
    waggonChangeOverTo
    vmax
    banWaggonType {
      id
      description
    }
    cargoBrakeTypes {
      id
      description
    }
    addition
    richtpunkts {
      ...cargoCompositionItemStretch
    }
    grenzpunkts {
      ...cargoCompositionItemStretch
    }
  }
  ${agencyBasic}
  ${cargoCompositionItemStretch}
  ${calendarWatermarkDetailedFragment}
  ${operationPoint}
`;


export const cargoTrainComposition = gql`
  fragment cargoTrainComposition on CargoTrainComposition {
    compositionItems {
      ...cargoTrainCompositionItem
    }
  }
  ${cargoTrainCompositionItem}
`;

export const goodTypeBasic = gql`
  fragment goodType on GoodType {
    id
    enee_id
    description
  }`;
